<template>
  <div>
    <div class="d-flex">
      <y-select
        v-model="v$.countryCode.$model"
        :items="translatedCountryCodes"
        item-title="text"
        persistent-hint
        required
        hide-details
        rounded="s-lg e-0"
        class="flex-grow-0 border-e-0"
      />
      <y-text-field
        ref="inputRef"
        v-model="v$.phoneNumber.$model"
        type="tel"
        :label="t('components.phoneNumber.label')"
        :prefix="v$.countryCode.$model"
        rounded="s-0 e-lg"
        class="flex-grow-1"
        min-width="150"
        hide-details
      />
    </div>
    <div style="height: 22px">
      <v-slide-y-transition>
        <p v-if="v$.phoneNumber.$errors.length > 0" class="text-error ps-4">
          <label class="text-caption"
            >{{ validationErrors(v$.phoneNumber.$errors).join(", ") }}&nbsp;</label
          >
        </p>
      </v-slide-y-transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ErrorObject, useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { parsePhoneNumber } from "awesome-phonenumber";
import { ref, watch, onMounted, onUpdated, computed } from "vue";
import { useI18n } from "vue-i18n";
import { phoneCountryCodes } from "@/utils/config";
import { i18nRequired, requiredField } from "@/utils/custom-validators";

const { t } = useI18n();

const model = defineModel<string | undefined>();

const form = ref({
  countryCode: "+44",
  phoneNumber: "",
});

const translatedCountryCodes = computed(() =>
  phoneCountryCodes.map((code) => ({
    text: t(`components.phoneNumber.countryCodes.${code.value.replace("+", "")}`),
    value: code.value,
  })),
);

const v$ = useVuelidate(
  {
    countryCode: { requiredField },
    phoneNumber: {
      required: i18nRequired,
      phoneNumberValidator: helpers.withMessage(
        t("components.phoneNumber.validation"),
        () => parsePhoneNumber(model.value ?? "").valid,
      ),
    },
  },
  form,
);
const splitPhoneNumber = () => {
  const phoneNumber = parsePhoneNumber(model.value ?? "");
  if (phoneNumber.valid) {
    form.value.countryCode = `+${phoneNumber.countryCode}`;
    form.value.phoneNumber = phoneNumber.number.significant;
  }
};
onMounted(splitPhoneNumber);
onUpdated(splitPhoneNumber);
watch(
  form,
  () => {
    if (!form.value.phoneNumber || form.value.phoneNumber === "") {
      model.value = undefined;
    } else {
      model.value = `${form.value.countryCode}${form.value.phoneNumber}`;
    }
  },
  { deep: true },
);

const validationErrors = (errors: ErrorObject[]) => errors.map((e) => e.$message.toString());

const inputRef = ref<HTMLInputElement | null>(null);
defineExpose<{ focus: () => void }>({
  focus: () => inputRef.value?.focus({ preventScroll: true }),
});
</script>
